import { useWizard } from "../wizard-state";
import type { Anticoagulante, YesNo, AutoYesNo } from "@/types/wizard";

type Props = { errors: Set<string> };

export function Step3Clinico({ errors }: Props) {
  const { data, set } = useWizard();
  const invalid = (k: string) => errors.has(k) ? "campo-requerido-error" : "";

  return (
    <div className="wizard-section-group">
      <div className="wizard-section">
        <h4 className="wizard-section-title">Signos vitales</h4>
        <div className="wizard-grid">
          <label className="form-field">
            <span>PA sistólica (mmHg) *</span>
            <input type="number" value={data.ps} onChange={(e) => set("ps", e.target.value)} className={invalid("ps")} />
          </label>
          <label className="form-field">
            <span>PA diastólica (mmHg) *</span>
            <input type="number" value={data.pd} onChange={(e) => set("pd", e.target.value)} className={invalid("pd")} />
          </label>
          <label className="form-field">
            <span>PA controlada</span>
            <select value={data.paControlada} onChange={(e) => set("paControlada", e.target.value as YesNo)}>
              <option value="si">Sí</option>
              <option value="no">No</option>
            </select>
          </label>
          <label className="form-field">
            <span>Frecuencia cardíaca *</span>
            <input type="number" value={data.fc} onChange={(e) => set("fc", e.target.value)} className={invalid("fc")} />
          </label>
          <label className="form-field">
            <span>Saturación O₂ *</span>
            <input type="number" value={data.saturacion} onChange={(e) => set("saturacion", e.target.value)} className={invalid("saturacion")} />
          </label>
          <label className="form-field">
            <span>Temperatura (°C) *</span>
            <input type="number" step="0.1" value={data.temp} onChange={(e) => set("temp", e.target.value)} className={invalid("temp")} />
          </label>
          <label className="form-field">
            <span>Glicemia (mg/dL) *</span>
            <input type="number" value={data.glicemia} onChange={(e) => set("glicemia", e.target.value)} className={invalid("glicemia")} />
          </label>
          <label className="form-field">
            <span>Glicemia controlada</span>
            <select value={data.glicemiaControlada} onChange={(e) => set("glicemiaControlada", e.target.value as YesNo)}>
              <option value="si">Sí</option>
              <option value="no">No</option>
            </select>
          </label>
          <label className="form-field">
            <span>Peso (kg)</span>
            <input type="number" step="0.1" value={data.peso} onChange={(e) => set("peso", e.target.value)} />
          </label>
        </div>
      </div>

      <div className="wizard-section">
        <h4 className="wizard-section-title">Chequeo oftalmológico</h4>
        <div className="wizard-grid">
          <label className="form-field">
            <span>Blefaritis *</span>
            <select value={data.blefaritis} onChange={(e) => set("blefaritis", e.target.value as "ninguna" | "leve" | "severa")} className={invalid("blefaritis")}>
              <option value="ninguna">Ninguna</option>
              <option value="leve">Leve</option>
              <option value="severa">Moderada / Severa (activa)</option>
            </select>
          </label>
          <label className="form-field">
            <span>Síntomas respiratorios</span>
            <select value={data.resp} onChange={(e) => set("resp", e.target.value as "ninguno" | "leve" | "severo")}>
              <option value="ninguno">Ninguno</option>
              <option value="leve">Leves sin fiebre</option>
              <option value="severo">Moderados/severos</option>
            </select>
          </label>
          <label className="form-field">
            <span>Piel</span>
            <select value={data.piel} onChange={(e) => set("piel", e.target.value as typeof data.piel)}>
              <option value="ninguna">Ninguna</option>
              <option value="herida_seca">Herida seca / cicatriz</option>
              <option value="dermatitis_no_infectada">Dermatitis no infectada</option>
              <option value="hongos_piel_unas">Hongos en piel/uñas</option>
              <option value="caspa_seborreica">Caspa seborreica</option>
              <option value="herida_infectada">Herida infectada</option>
              <option value="herida_abierta">Herida abierta</option>
              <option value="celulitis">Celulitis orbitaria/periorbitaria</option>
              <option value="ulcera_mmii">Úlcera en miembros inferiores</option>
            </select>
          </label>
          <label className="form-field">
            <span>Estado dental</span>
            <select value={data.dental} onChange={(e) => set("dental", e.target.value as typeof data.dental)}>
              <option value="ninguno">Sin hallazgos</option>
              <option value="deterioro_sin_infeccion">Deterioro sin infección</option>
              <option value="dentadura_riesgo">Dentadura con riesgo infeccioso</option>
              <option value="infeccion_activa">Infección activa</option>
            </select>
          </label>
          <label className="form-field">
            <span>Conjuntivitis</span>
            <select value={data.conjuntivitis} onChange={(e) => set("conjuntivitis", e.target.value as typeof data.conjuntivitis)}>
              <option value="ninguna">Ninguna</option>
              <option value="alergica_leve">Alérgica leve</option>
              <option value="activa">Activa</option>
            </select>
          </label>
          <label className="form-field">
            <span>Estado higiénico</span>
            <select value={data.estadoHigienico} onChange={(e) => set("estadoHigienico", e.target.value as typeof data.estadoHigienico)}>
              <option value="adecuado">Adecuado</option>
              <option value="regular">Regular</option>
              <option value="inadecuado">Inadecuado</option>
            </select>
          </label>
        </div>
      </div>

      <div className="wizard-section">
        <h4 className="wizard-section-title">Historia clínica</h4>
        <div className="wizard-grid">
          <label className="form-field form-field--full">
            <span>Antecedentes *</span>
            <textarea rows={2} value={data.antecedentes} onChange={(e) => set("antecedentes", e.target.value)} className={invalid("antecedentes")} />
          </label>
          <label className="form-field form-field--full">
            <span>Medicamentos *</span>
            <textarea rows={2} value={data.medicamentos} onChange={(e) => set("medicamentos", e.target.value)} className={invalid("medicamentos")} />
          </label>
          <label className="form-field form-field--full">
            <span>Alergias *</span>
            <textarea rows={2} value={data.alergias} onChange={(e) => set("alergias", e.target.value)} className={invalid("alergias")} />
          </label>
          <label className="form-field form-field--full">
            <span>Cirugías previas *</span>
            <textarea rows={2} value={data.cirugiasPrevias} onChange={(e) => set("cirugiasPrevias", e.target.value)} className={invalid("cirugiasPrevias")} />
          </label>
        </div>

        <div className="wizard-checkboxes mt-8">
          <strong>Antecedentes marcados:</strong>
          {([
            ["antDiabetes", "Diabetes"], ["antHipertension", "Hipertensión"],
            ["antTiroidea", "Enfermedad tiroidea"], ["antRenal", "Enf. renal"],
            ["antGripa", "Gripa/resfrío reciente"], ["antACV", "ACV"],
            ["antInfartos", "Infartos previos"], ["antTromboembolismo", "Tromboembolismo"],
            ["antRetrasoMental", "Retraso mental"], ["antDown", "Síndrome de Down"],
            ["antColostomia", "Colostomía"], ["antCrisisAnsiosa", "Crisis ansiosa"],
          ] as const).map(([key, label]) => (
            <label key={key}>
              <input type="checkbox" checked={data[key] as boolean} onChange={(e) => set(key, e.target.checked as never)} />
              {label}
            </label>
          ))}
        </div>

        <div className="wizard-grid mt-8">
          <label className="form-field">
            <span>Cardio (marcar y detallar)</span>
            <input type="checkbox" checked={data.antCardio} onChange={(e) => set("antCardio", e.target.checked)} />
            {data.antCardio && (
              <input placeholder="¿Cuál?" value={data.antCardioCual} onChange={(e) => set("antCardioCual", e.target.value)} />
            )}
          </label>
          <label className="form-field">
            <span>Pulmonar (marcar y detallar)</span>
            <input type="checkbox" checked={data.antPulmonar} onChange={(e) => set("antPulmonar", e.target.checked)} />
            {data.antPulmonar && (
              <input placeholder="¿Cuál?" value={data.antPulmonarCual} onChange={(e) => set("antPulmonarCual", e.target.value)} />
            )}
          </label>
          <label className="form-field form-field--full">
            <span>Otra condición relevante</span>
            <input value={data.antOtra} onChange={(e) => set("antOtra", e.target.value)} />
          </label>
        </div>
      </div>

      <div className="wizard-section">
        <h4 className="wizard-section-title">Anticoagulantes y suspensiones</h4>
        <div className="wizard-grid">
          <label className="form-field">
            <span>Anticoagulante</span>
            <select value={data.anticoagulante} onChange={(e) => set("anticoagulante", e.target.value as Anticoagulante)}>
              <option value="ninguno">Ninguno</option>
              <option value="aspirina">Aspirina / ASA</option>
              <option value="clopidogrel">Clopidogrel</option>
              <option value="warfarina">Warfarina</option>
              <option value="apixaban">Apixaban</option>
              <option value="otro">Otro</option>
            </select>
          </label>
          {data.anticoagulante === "otro" && (
            <label className="form-field">
              <span>¿Cuál?</span>
              <input value={data.anticoagulanteOtro} onChange={(e) => set("anticoagulanteOtro", e.target.value)} />
            </label>
          )}
          {data.anticoagulante === "aspirina" && (
            <label className="form-field">
              <span>Aspirina suspendida ≥5 días</span>
              <select value={data.aspirinaSuspendida} onChange={(e) => set("aspirinaSuspendida", e.target.value as AutoYesNo)}>
                <option value="na">N/A</option>
                <option value="si">Sí</option>
                <option value="no">No</option>
              </select>
            </label>
          )}
          {["clopidogrel", "warfarina", "apixaban", "otro"].includes(data.anticoagulante) && (
            <label className="form-field">
              <span>Autorización anestesiología</span>
              <select value={data.autorizAnestAnticoag} onChange={(e) => set("autorizAnestAnticoag", e.target.value as AutoYesNo)}>
                <option value="na">N/A</option>
                <option value="si">Sí</option>
                <option value="no">No</option>
              </select>
            </label>
          )}
        </div>
        <div className="wizard-checkboxes mt-8">
          <label>
            <input type="checkbox" checked={data.suspPreviasDescontrol} onChange={(e) => set("suspPreviasDescontrol", e.target.checked)} />
            Suspensiones previas con descontrol
          </label>
          <label>
            <input type="checkbox" checked={data.suspNoAutorizadaMedEsenciales} onChange={(e) => set("suspNoAutorizadaMedEsenciales", e.target.checked)} />
            Suspensión no autorizada de medicamentos esenciales
          </label>
        </div>
      </div>
    </div>
  );
}
